import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { LifeStoryProvider } from './contexts/LifeStoryContext';
import { CommunityProvider } from './contexts/CommunityContext';

// Screens
import LandingPage from './screens/LandingPage';
import HomeScreen from './screens/HomeScreen';
import RecordingScreen from './screens/RecordingScreen';
import SectionsScreen from './screens/SectionsScreen';
import POAConsentScreen from './screens/POAConsentScreen';
import LoginScreen from './screens/LoginScreen';
import AuthComplete from './screens/AuthComplete';
import NotFoundScreen from './screens/NotFoundScreen';
import ContributePage from './screens/ContributePage';
import ContributionsScreen from './screens/ContributionsScreen';
import SummariesScreen from './screens/SummariesScreen';
import CareCardPage from './screens/CareCardPage';
import ConversationStartersPage from './screens/ConversationStartersPage';
import PrintShopScreen from './screens/PrintShopScreen';
import BookPreviewPage from './screens/BookPreviewPage';

// Family Portal Screens
import FamilyPortalPage from './screens/FamilyPortalPage';
import FamilyStoryView from './screens/FamilyStoryView';
import FamilySummariesView from './screens/FamilySummariesView';
import FamilyBookView from './screens/FamilyBookView';
import FamilyContributePage from './screens/FamilyContributePage';

// Community Screens (Phase 1)
import CommunityDashboard from './screens/CommunityDashboard';
import CommunitySetupPage from './screens/CommunitySetupPage';
import CommunitiesListPage from './screens/CommunitiesListPage';
import ResidentImportPage from './screens/ResidentImportPage';
import StaffManagementPage from './screens/StaffManagementPage';
import QuestionManagementPage from './screens/QuestionManagementPage';
import StaffInvitePage from './screens/StaffInvitePage';
import ResidentDetailPage from './screens/ResidentDetailPage';
import CommunitySettingsPage from './screens/CommunitySettingsPage';
import CareSupplementScreen from './screens/CareSupplementScreen';

// Components
import ProtectedRoute from './components/ProtectedRoute';
import AuthenticatedRedirect from './components/AuthenticatedRedirect';
import CommunityRedirect from './components/CommunityRedirect';

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginScreen />} />
          <Route path="/auth/complete" element={<AuthComplete />} />
          <Route path="/contribute/:token" element={<ContributePage />} />
          <Route path="/care-card/:token" element={<CareCardPage />} />
          <Route path="/conversation-starters/:token" element={<ConversationStartersPage />} />
          <Route path="/book-preview/:token" element={<BookPreviewPage />} />
          
          {/* Staff invite acceptance (public — handles its own auth) */}
          <Route path="/staff-invite/:token" element={<StaffInvitePage />} />
          
          {/* Family Portal routes (public - token-based auth) */}
          <Route path="/family/:token" element={<FamilyPortalPage />} />
          <Route path="/family/:token/story" element={<FamilyStoryView />} />
          <Route path="/family/:token/summaries" element={<FamilySummariesView />} />
          <Route path="/family/:token/book" element={<FamilyBookView />} />
          <Route path="/family/:token/contribute" element={<FamilyContributePage />} />
          
          {/* Landing page: unauthenticated → landing, 
              community staff → community dashboard, 
              regular user → home screen */}
          <Route
            path="/"
            element={
              <AuthenticatedRedirect 
                authenticated={<CommunityRedirect />}
                unauthenticated={<LandingPage />}
              />
            }
          />

          {/* Super Admin: All communities list */}
          <Route
            path="/communities"
            element={
              <ProtectedRoute>
                <CommunitiesListPage />
              </ProtectedRoute>
            }
          />

          {/* Community setup (for creating a new community) */}
          <Route
            path="/community/setup"
            element={
              <ProtectedRoute>
                <CommunitySetupPage />
              </ProtectedRoute>
            }
          />
          
          {/* Community Dashboard */}
          <Route
            path="/community/:communityId"
            element={
              <ProtectedRoute>
                <CommunityProvider>
                  <CommunityDashboard />
                </CommunityProvider>
              </ProtectedRoute>
            }
          />

          {/* Community Settings */}
          <Route
            path="/community/:communityId/settings"
            element={
              <ProtectedRoute>
                <CommunityProvider>
                  <CommunitySettingsPage />
                </CommunityProvider>
              </ProtectedRoute>
            }
          />

          {/* Community Question Management */}
          <Route
            path="/community/:communityId/questions"
            element={
              <ProtectedRoute>
                <QuestionManagementPage />
              </ProtectedRoute>
            }
          />

          {/* Community Resident Import */}
          <Route
            path="/community/:communityId/import"
            element={
              <ProtectedRoute>
                <CommunityProvider>
                  <ResidentImportPage />
                </CommunityProvider>
              </ProtectedRoute>
            }
          />

          {/* Community Resident Detail Page */}
          <Route
            path="/community/:communityId/resident/:storyId"
            element={
              <ProtectedRoute>
                <ResidentDetailPage />
              </ProtectedRoute>
            }
          />

          {/* Care & Comfort Supplement (staff-completed, sensitive) */}
          <Route
            path="/community/:communityId/resident/:storyId/care-supplement"
            element={
              <ProtectedRoute>
                <CareSupplementScreen />
              </ProtectedRoute>
            }
          />
          
          {/* Standalone life story routes */}
          <Route
            path="/story/:storyId"
            element={
              <ProtectedRoute>
                <LifeStoryProvider>
                  <RecordingScreen />
                </LifeStoryProvider>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/story/:storyId/sections"
            element={
              <ProtectedRoute>
                <LifeStoryProvider>
                  <SectionsScreen />
                </LifeStoryProvider>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/story/:storyId/poa-consent"
            element={
              <ProtectedRoute>
                <LifeStoryProvider>
                  <POAConsentScreen />
                </LifeStoryProvider>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/story/:storyId/contributions"
            element={
              <ProtectedRoute>
                <ContributionsScreen />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/story/:storyId/summaries"
            element={
              <ProtectedRoute>
                <SummariesScreen />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/story/:storyId/print"
            element={
              <ProtectedRoute>
                <PrintShopScreen />
              </ProtectedRoute>
            }
          />
          
          {/* Legacy community routes — sub-routes still needed */}
          <Route
            path="/community/:communityId/resident/:residentId/sections"
            element={
              <ProtectedRoute>
                <LifeStoryProvider>
                  <SectionsScreen />
                </LifeStoryProvider>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/community/:communityId/resident/:residentId/poa-consent"
            element={
              <ProtectedRoute>
                <LifeStoryProvider>
                  <POAConsentScreen />
                </LifeStoryProvider>
              </ProtectedRoute>
            }
          />
          
          {/* 404 */}
          <Route path="*" element={<NotFoundScreen />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
